import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MenuComponentBase } from '../../../../../shared/base-classes/menu-component-base';
import { PageSizeComponent } from '../../../../../shared/components/page-size/page-size.component';
import { PaginationComponent } from '../../../../../shared/components/pagination/pagination.component';
import { FormsModule } from '@angular/forms';
import { CDFILeadsQuota } from '../../models/CDFI-leads-quota';
import { CdfiLeadsQuotaService } from '../../services/cdfi-leads-quota.service';
import { forkJoin, takeUntil } from 'rxjs';
import { SvgIconComponent } from '../../../../../shared/components/svg-icon/svg-icon.component';
import { PopupComponent } from '../../../../../shared/components/popup/popup.component';
import { SourceTypes } from '../../../../../shared/models/source-types';
import { Auth0Connection } from '../../../../../shared/models/auth0-connection';
import { ConnectionQuotaComponent } from '../connection-quota/connection-quota.component';
import { ManageConnectionService } from '../../../../manage-connection-group/services/manage-connection.service';
import { SearchInputComponent } from '../../../../../shared/components/search-input/search-input.component';
import { AuditLogService } from '../../../../../shared/services/audit-log/audit-log.service';

@Component({
  selector: 'app-cdfi-leads-quota',
  standalone: true,
  imports: [
    PageSizeComponent,
    PaginationComponent,
    FormsModule,
    SvgIconComponent,
    PopupComponent,
    ConnectionQuotaComponent,
    SearchInputComponent,
  ],
  templateUrl: './cdfi-leads-quota.component.html',
  styleUrl: './cdfi-leads-quota.component.scss',
})
export class CdfiLeadsQuotaComponent
  extends MenuComponentBase
  implements OnInit, OnDestroy
{
  searchQuery = '';
  currentPage = 1;
  pageSize = 50;
  loading = true;
  leadsQuota: CDFILeadsQuota[] = [];
  totalCDFILeadsQuotaLogs = 0;
  deleteError: string | null = null;
  quotaToDelete: {
    isConnectionQuotaDelete: boolean;
    connectionId: number;
    sourceType?: number;
  } | null = null;
  sourceTypes: SourceTypes[] = [];
  showAddConnectionQuotaPopup = false;
  connections: Auth0Connection[] = [];
  filteredConnections: Auth0Connection[] = [];
  connectionQuotaError: string | null = null;
  newConnectionQuota = {
    connectionId: null as number | null,
    sourceQuotas: [
      { sourceType: null as number | null, maxQuota: null as number | null },
    ],
  };

  cdfiLeadsQuoataService = inject(CdfiLeadsQuotaService);
  manageConnectionService = inject(ManageConnectionService);
  auditLogService = inject(AuditLogService);

  override ngOnInit(): void {
    super.ngOnInit();
    this.loading = true;

    const quotaRequest$ = this.cdfiLeadsQuoataService.getLeadsQuota(
      this.currentPage,
      this.pageSize,
      this.searchQuery,
    );

    const sourceTypesRequest$ = this.cdfiLeadsQuoataService.getAllSourceTypes();
    const connectionsRequest$ =
      this.manageConnectionService.getConnectionsFromDB();

    forkJoin({
      quotas: quotaRequest$,
      sourceTypes: sourceTypesRequest$,
      connections: connectionsRequest$,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.leadsQuota = results.quotas.results;
          this.totalCDFILeadsQuotaLogs = results.quotas.count;
          this.sourceTypes = results.sourceTypes.filter(
            (s) => s.source_display_name !== 'SME',
          );
          this.connections = results.connections;
          this.filteredConnections = results.connections;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading initial data:', error);
          this.loading = false;
        },
      });
  }

  loadLeadsQuota(pageNumber: number, pageSize: number): void {
    const searchParam = this.searchQuery || undefined;
    this.cdfiLeadsQuoataService
      .getLeadsQuota(pageNumber, pageSize, searchParam)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.leadsQuota = response.results;
          this.totalCDFILeadsQuotaLogs = response.count;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadLeadsQuota(this.currentPage, this.pageSize);
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.loadLeadsQuota(this.currentPage, this.pageSize);
    }
  }

  onSearchQueryChange(query: string) {
    this.searchQuery = query;
    this.currentPage = 1;
    this.loadLeadsQuota(this.currentPage, this.pageSize);
  }

  showAddConnectionQuotaPopupHandler(): void {
    // Filter out connections that already have quotas
    this.filteredConnections = this.connections.filter(
      (conn) =>
        !this.leadsQuota.some(
          (quota) => quota.connection_id === Number(conn.connection_id),
        ),
    );

    this.newConnectionQuota = {
      connectionId: null,
      sourceQuotas: [{ sourceType: null, maxQuota: null }],
    };
    this.connectionQuotaError = null;
    this.showAddConnectionQuotaPopup = true;
  }

  addSourceQuotaField(): void {
    this.newConnectionQuota.sourceQuotas.push({
      sourceType: null,
      maxQuota: null,
    });
  }

  removeSourceQuotaField(idx: number): void {
    if (this.newConnectionQuota.sourceQuotas.length > 1) {
      this.newConnectionQuota.sourceQuotas.splice(idx, 1);
    }
  }

  createConnectionQuota(): void {
    if (!this.newConnectionQuota.connectionId) {
      this.connectionQuotaError = 'Please select a connection.';
      return;
    }
    if (
      this.newConnectionQuota.sourceQuotas.some(
        (q) => !q.sourceType || !q.maxQuota,
      )
    ) {
      this.connectionQuotaError = 'Please fill in all source types and quotas.';
      return;
    }
    // Prevent duplicate source types
    const sourceTypeSet = new Set();
    for (const q of this.newConnectionQuota.sourceQuotas) {
      if (sourceTypeSet.has(q.sourceType)) {
        this.connectionQuotaError = 'Duplicate source types are not allowed.';
        return;
      }
      sourceTypeSet.add(q.sourceType);
    }
    const createLeadQuotaModel = this.newConnectionQuota.sourceQuotas.map(
      (q) => ({
        source_type: q.sourceType!,
        max_quota: q.maxQuota!,
      }),
    );
    this.cdfiLeadsQuoataService
      .createLeadsQuota(
        this.newConnectionQuota.connectionId,
        createLeadQuotaModel,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showAddConnectionQuotaPopup = false;
          this.loadLeadsQuota(this.currentPage, this.pageSize);

          const selectedConnection = this.connections.find(
            (c) =>
              Number(c.connection_id) === this.newConnectionQuota.connectionId,
          );

          this.auditLogService.logFeature({
            featureName: 'cdfi_leads_quota',
            action: 'add',
            meta: {
              connectionId: String(this.newConnectionQuota.connectionId),
              connectionName: selectedConnection?.connection_display_name || '',
              quotasCount: String(this.newConnectionQuota.sourceQuotas.length),
            },
            description: 'User adds CDFI leads quota',
          });
        },
        error: (err) => {
          const errmsg = err?.error[0]?.non_field_errors[0];
          this.connectionQuotaError = 'Failed to create quota. ' + errmsg;
        },
      });
  }

  getAvailableSourceTypesForDropdown(currentIndex: number): SourceTypes[] {
    // Get already selected source types from other dropdowns
    const selectedSourceTypes = this.newConnectionQuota.sourceQuotas
      .filter((_, index) => index !== currentIndex) // Exclude current dropdown
      .map((quota) => quota.sourceType)
      .filter((sourceType) => sourceType !== null);

    // Filter out already selected source types
    return this.sourceTypes.filter(
      (sourceType) => !selectedSourceTypes.includes(sourceType.source_type),
    );
  }

  canAddMoreSourceTypes(): boolean {
    // Check if any current quotas are incomplete
    const hasIncomplete = this.newConnectionQuota.sourceQuotas.some(
      (quota) => quota.sourceType === null || quota.maxQuota === null,
    );
    if (hasIncomplete) {
      return false;
    }

    // Get all currently selected source types
    const selectedSourceTypes = this.newConnectionQuota.sourceQuotas
      .map((quota) => quota.sourceType)
      .filter((sourceType) => sourceType !== null);

    // Check if there are any source types left that haven't been selected
    return this.sourceTypes.some(
      (sourceType) => !selectedSourceTypes.includes(sourceType.source_type),
    );
  }

  removeConnectionQuota(connectionId: number): void {
    this.leadsQuota = this.leadsQuota.filter(
      (quota) => quota.connection_id !== connectionId,
    );
  }

  reloadLeadsQuota(): void {
    this.loadLeadsQuota(this.currentPage, this.pageSize);
  }
}
